<!-- eslint-disable vue/multi-word-component-names -->
<template>
  <div id="Shipping">
    <MyBanner
      v-if="$t('banner.accessDcHistoricalPage') !== ''"
      :text="$t('banner.accessDcHistoricalPage')"
    />
    <MyHeaderPage
      :title="$t('accessDc.historicalAccessDc.title')"
      :subtitle="$t('accessDc.historicalAccessDc.subTitle')"
    />
    <br /><br />

    <div id="TicketsList">
      <div class="listTicket">
        <div class="mdl-grid">
          <!-- Search -->
          <div class="mdl-cell mdl-cell--6-col mdl-cell--12-col-phone">
            <div class="filterTickets">
              <div class="contentInputFilter">
                <div class="contentIcon">
                  <span class="material-icons">search</span>
                </div>
                <div class="contentInput">
                  <input
                    type="text"
                    :placeholder="$t('actions.text_search_placeholder')"
                    v-model="searchString"
                  />
                </div>
              </div>
            </div>
          </div>

          <!-- Date from -->
          <div class="mdl-cell mdl-cell--3-col mdl-cell--12-col-phone">
            <div class="cdl-input" style="margin-top: 0">
              <input
                type="date"
                name="dateFrom"
                class="not-empty"
                v-model="dateFrom"
                style="background-color: white"
                @change="onDateChange"
              />
              <label>{{ $t('reports.filter.dateFrom') }}</label>
              <span class="error" v-if="dateError">
                {{ $t('reports.filter.dateFrom') }} {{ $t('reports.filter.isRequired') }}
              </span>
            </div>
          </div>

          <!-- Date to -->
          <div class="mdl-cell mdl-cell--3-col mdl-cell--12-col-phone">
            <div class="cdl-input" style="margin-top: 0">
              <input
                type="date"
                name="dateTo"
                class="not-empty"
                v-model="dateTo"
                style="background-color: white"
                @change="onDateChange"
              />
              <label>{{ $t('reports.filter.dateTo') }}</label>
            </div>
          </div>

          <!-- CSV Export -->
          <div class="mdl-cell mdl-cell--2-col mdl-cell--10-offset mdl-cell--12-col-tablet mdl-cell--12-col-phone">
            <ActionButton
              :title="$t('users.downLoadCSV')"
              icon="download"
              icon-position="left"
              custom-class="cdlButton"
              size="medium"
              :loading="isExporting"
              :disabled="!datesValid || isExporting"
              @click="exportCsv"
            />
          </div>
        </div>

        <DataTable
          :items="displayItems"
          :loading="loaded"
          :total-page="displayTotalPage"
          :current-page="currentPage"
          :page-size="PAGE_SIZE"
          :empty-colspan="5"
          :empty-message="$t('accessDc.historicalAccessDc.noUsers')"
          :error-message="$t('tickets.error')"
          :view-error="viewError"
          :headers="tableHeaders"
          :columns="tableColumns"
          :mobile-component="AccessDcMobileCard"
          :mobile-props="(item) => ({ item })"
          @change-page="onChangePage"
          @prev-page="onPrevPage"
          @next-page="onNextPage"
        />
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, getCurrentInstance } from 'vue'
import { useI18n } from 'vue-i18n'
import apiClient from '@/services/apiClient'
import { useDownload } from '@/composables/useDownload'
import MyBanner from '@/components/MyBanner.vue'
import MyHeaderPage from '@/components/MyHeaderPage.vue'
import DataTable from '@/components/Primitives/Tables/DataTable.vue'
import ActionButton from '@/components/Primitives/Buttons/ActionButton.vue'
import AccessDcMobileCard from '@/components/AccessDc/AccessDcMobileCard.vue'
import '@/assets/scss/Shipping.scss'
import '@/assets/scss/TicketsList.scss'

const { t } = useI18n()
const { proxy } = getCurrentInstance()

const PAGE_SIZE = 10

/* ── Date helpers ─────────────────────────────────────────────────────────── */

function toLocalDateInput(d) {
  const y = d.getFullYear()
  const m = String(d.getMonth() + 1).padStart(2, '0')
  const day = String(d.getDate()).padStart(2, '0')
  return `${y}-${m}-${day}`
}

function parseDate(str) {
  if (!str) return null
  const d = new Date(str.replace(' ', 'T'))
  return isNaN(d.getTime()) ? null : d
}

function formatDate(str) {
  const d = parseDate(str)
  if (!d) return '--/--/----'
  const y = d.getFullYear()
  const m = String(d.getMonth() + 1).padStart(2, '0')
  const day = String(d.getDate()).padStart(2, '0')
  return `${y}-${m}-${day}`
}

function formatTime(str) {
  const d = parseDate(str)
  if (!d) return '--:--:--'
  return d.toTimeString().slice(0, 8)
}

/* ── State ───────────────────────────────────────────────────────────────── */

const sixMonthsAgo = new Date()
sixMonthsAgo.setMonth(sixMonthsAgo.getMonth() - 6)

const dateFrom = ref(toLocalDateInput(sixMonthsAgo))
const dateTo = ref(toLocalDateInput(new Date()))
const searchString = ref('')
const currentPage = ref(1)
const totalPage = ref(1)
const allItems = ref([])
const loaded = ref(true)
const viewError = ref(false)
const dateError = ref(false)

/* ── Download ────────────────────────────────────────────────────────────── */

const { isDownloading: isExporting, download: downloadCsv } = useDownload()

/* ── Computed ────────────────────────────────────────────────────────────── */

const datesValid = computed(() => !!dateFrom.value && !!dateTo.value)

const searchActive = computed(() => searchString.value.length > 3)

const displayItems = computed(() => {
  if (!searchActive.value) return allItems.value
  const s = searchString.value.toLowerCase()
  return allItems.value.filter((el) =>
    [el.surname, el.name, el.from, el.to].some((v) => v?.toLowerCase().includes(s))
  )
})

const displayTotalPage = computed(() => (searchActive.value ? 1 : totalPage.value))

const tableHeaders = computed(() => [
  t('accessDc.historicalAccessDc.col.surname'),
  t('accessDc.historicalAccessDc.col.name'),
  t('accessDc.historicalAccessDc.col.date'),
  t('accessDc.historicalAccessDc.col.from'),
  t('accessDc.historicalAccessDc.col.to'),
])

const tableColumns = [
  { key: 'surname' },
  { key: 'name' },
  { key: 'from', format: (v) => formatDate(v) },
  { key: 'from', format: (v) => formatTime(v) },
  { key: 'to', format: (v) => formatTime(v) },
]

/* ── API ─────────────────────────────────────────────────────────────────── */

async function getAccessDcList() {
  if (!datesValid.value) {
    dateError.value = true
    return
  }
  dateError.value = false
  loaded.value = false
  viewError.value = false
  allItems.value = []

  try {
    const { data } = await apiClient.get('front/v1/dc-accesses', {
      params: {
        from: dateFrom.value,
        to: dateTo.value,
        page: currentPage.value,
        per_page: PAGE_SIZE,
      },
    })

    const payload = data ?? []
    const items = Array.isArray(payload)
      ? payload
      : Array.isArray(payload.data)
        ? payload.data
        : []
    const total = Number(payload?.total ?? items.length)
    const perPage = Number(payload?.per_page ?? PAGE_SIZE)
    allItems.value = items
    totalPage.value = perPage > 0 ? Math.ceil(total / perPage) : 1
    currentPage.value = Number(payload?.current_page ?? currentPage.value) || 1
  } catch (err) {
    const status = err?.response?.status
    if (status === 404 || status === 403) {
      allItems.value = []
    } else {
      viewError.value = true
      setTimeout(() => { viewError.value = false }, 5000)
    }
  } finally {
    loaded.value = true
  }
}

/* ── Handlers ────────────────────────────────────────────────────────────── */

function onDateChange() {
  currentPage.value = 1
  getAccessDcList()
}

function onChangePage(page) {
  currentPage.value = parseInt(page)
  getAccessDcList()
}

function onPrevPage() {
  if (currentPage.value <= 1) return
  currentPage.value -= 1
  getAccessDcList()
}

function onNextPage() {
  if (currentPage.value >= totalPage.value) return
  currentPage.value += 1
  getAccessDcList()
}

async function exportCsv() {
  if (!datesValid.value) return
  await downloadCsv(
    () => apiClient.get('front/v1/dc-accesses', {
      params: { from: dateFrom.value, to: dateTo.value, csv: 'true' },
      headers: { Accept: 'text/csv' },
      responseType: 'blob',
    }),
    { filename: 'access_historical.csv' }
  )
}

/* ── Lifecycle ───────────────────────────────────────────────────────────── */

onMounted(() => {
  window.scrollTo(0, 0)
  proxy.$IsLoggedIn()
  getAccessDcList()
})
</script>
